import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import Highcharts from 'highcharts'
import exporting from 'highcharts/modules/exporting.js'
import offlineExporting from 'highcharts/modules/offline-exporting.js'
import exportData from 'highcharts/modules/export-data.js'
import HighchartsReact from 'highcharts-react-official'
import { Spin, Collapse, Panel, RadioGroup, RadioButton } from '../common/Ant'
import { H4 } from '../common/Headers'
import { hasRole } from '../../utils/hasPermission'
import { getSensorReading } from '../../utils/sensorData'
import { dateTimeFormatter } from '../../utils/date'
import { createQueryString, parseQueryParams } from '../../utils/queryParams'
import {
  SENSOR_READING_TYPES,
  FLOW_SENSOR_READING_TYPES,
  ROLES,
  COLORS,
} from '../../constants'
import anomalyMarker from '../../assets/anomaly-marker.png'

exporting(Highcharts)
offlineExporting(Highcharts)
exportData(Highcharts)

const CHART_FONT_FAMILY =
  "-apple-system, BlinkMacSystemFont, 'avenir next', avenir, 'helvetica neue', helvetica, ubuntu, roboto, noto, 'segoe ui', arial, sans-serif"

const adminCalibrationReadings = [
  SENSOR_READING_TYPES.TEMPERATURE_UNCALIBRATED,
  SENSOR_READING_TYPES.HUMIDITY_UNCALIBRATED,
  SENSOR_READING_TYPES.HUMIDITY_CALIBRATED_1PT,
  SENSOR_READING_TYPES.HUMIDITY_CALIBRATED_2PT,
]

const SensorChart = ({
  data,
  columns,
  isLoading,
  updateQueryParams,
  locationName,
  podPillarId,
  isLeakPod,
  isEvacPod,
}) => {
  const [visibleReadings, setVisibleReadings] = useState([])
  // this is strange but calling `setVisibleReadings` from within the chart
  // does not trigger a state update in this component. this is the only method
  // I found to be able to track clicks on the chart legend and update the URL
  // with the selected sensor params
  const [chartUpdated, setChartUpdated] = useState(Math.random())
  const [chartType, setChartType] = useState('line')
  const isAdmin = hasRole(ROLES.PILLAR_ADMIN, ROLES.SUPER_ADMIN)

  useEffect(() => {
    const { visibleReadings } = parseQueryParams(global.location)

    if (!!visibleReadings && visibleReadings.length > 0) {
      setVisibleReadings(
        Array.isArray(visibleReadings) ? visibleReadings : [visibleReadings]
      )
    } else {
      const defaults = [
        SENSOR_READING_TYPES.TEMPERATURE,
        SENSOR_READING_TYPES.HUMIDITY,
        ...(isLeakPod
          ? [
              SENSOR_READING_TYPES.BATTERY_NORMAL,
              SENSOR_READING_TYPES.LEAK_VALUE,
              SENSOR_READING_TYPES.LEAK_EVENT,
            ]
          : [
              SENSOR_READING_TYPES.DUST,
              SENSOR_READING_TYPES.CARBON_MONOXIDE,
              SENSOR_READING_TYPES.PRESSURE,
            ]),
      ]

      if (isAdmin) {
        adminCalibrationReadings.forEach(reading => {
          if (columns.includes(reading)) {
            defaults.push(reading)
          }
        })
      }

      setVisibleReadings(defaults)
    }
  }, [isLeakPod, isAdmin, columns])

  useEffect(() => {
    const {
      range,
      endTime,
      startTime,
      smooth,
      smoothInterval,
    } = parseQueryParams(global.location)

    updateQueryParams({
      search: createQueryString({
        range,
        endTime,
        startTime,
        smooth,
        smoothInterval,
        visibleReadings,
      }),
    })
  }, [chartUpdated, visibleReadings, updateQueryParams])

  const blackListedReadings = [
    SENSOR_READING_TYPES.DP_ALARM,
    SENSOR_READING_TYPES.TEMPERATURE_LMT_87,
    SENSOR_READING_TYPES.GATEWAY_MODULE_ID,
    SENSOR_READING_TYPES.DP_LOW,
    SENSOR_READING_TYPES.DP_HIGH,
    SENSOR_READING_TYPES.DP_AVG,
    SENSOR_READING_TYPES.STATUS_FLAGS,
    SENSOR_READING_TYPES.ANOMALY_READING_TYPE,
    SENSOR_READING_TYPES.ANOMALY,
    FLOW_SENSOR_READING_TYPES.SWITCH_STATUS,
    FLOW_SENSOR_READING_TYPES.VALVE_STATUS,
    ...(hasRole(ROLES.SUPER_ADMIN)
      ? []
      : [
        //   SENSOR_READING_TYPES.BATTERY_NORMAL,
        //   SENSOR_READING_TYPES.BATTERY_LOADED,
        //   SENSOR_READING_TYPES.BATTERY_DROP,
        ]),
  ]

  const visibleColumns = columns.filter(
    x => blackListedReadings.indexOf(x) === -1
  )

  const chartTitle = locationName
    ? `${locationName} — ${podPillarId}`
    : podPillarId

  // TODO build default opts object and extend for each chart??
  const chartOptions = {
    credits: { enabled: false },
    exporting: {
      filename: chartTitle,
      buttons: {
        contextButton: {
          menuItems: [
            'viewFullscreen',
            'printChart',
            'separator',
            'downloadPNG',
            'downloadJPEG',
            'separator',
            'downloadCSV',
          ],
        },
      },
    },
    chart: {
      animation: false,
      zoomType: 'x',
      type: chartType,
      style: {
        fontFamily: CHART_FONT_FAMILY,
      },
    },
    plotOptions: {
      series: {
        events: {
          legendItemClick: ({ target: { userOptions } }) => {
            if (!userOptions.visible) {
              visibleReadings.push(userOptions.columnName)
            } else {
              const index = visibleReadings.indexOf(userOptions.columnName)
              visibleReadings.splice(index, 1)
            }

            setVisibleReadings(visibleReadings)
          },
        },
      },
    },
    title: {
      text: chartTitle,
    },
    yAxis: {
      gridLineWidth: 1,
      gridLineDashStyle: 'longdash',
      labels: {
        style: {
          color: COLORS.BLACK_80,
          fontSize: '0.75rem',
        },
      },
      title: {
        text: null,
      },
    },
    xAxis: {
      gridLineWidth: 1,
      gridLineDashStyle: 'longdash',
      type: 'datetime',
      labels: {
        style: {
          color: COLORS.BLACK_80,
          fontSize: '0.75rem',
        },
      },
      title: {
        text: null,
      },
    },
    tooltip: {
      style: {
        fontFamily: CHART_FONT_FAMILY,
        fontSize: '0.875rem',
        color: COLORS.BLACK_80,
      },
      formatter: function() {
        const date = Highcharts.dateFormat('%A, %b %e %Y, %H:%M', this.x)
        const suffix = (this.series.tooltipOptions && this.series.tooltipOptions.valueSuffix) || ''
        const value = Highcharts.numberFormat(this.y, 2, '.', ',')
        return (
          `<span style="font-size: 0.875rem; margin-bottom: 0.875rem">${date}</span><br/>` +
          `<span style="color:${this.point.color}">●</span> ${this.series.name}: <b>${value}${suffix}</b>`
        )
      },
    },
    legend: {
      verticalAlign: 'top',
      itemStyle: {
        fontSize: '0.75rem',
        color: COLORS.BLACK_80,
      },
      itemHoverStyle: {
        color: COLORS.MOON_GRAY,
      },
    },
    series: visibleColumns.map(x => {
      const readingData = getSensorReading(x)
      const zones =
        x === SENSOR_READING_TYPES.LIGHT
          ? [
              {
                // 5 foot-candles = 7.879875854099227 uW/cm2
                value: 7.879875854099227,
                color: COLORS.DARK_RED,
              },
              {
                // 30 foot-candles = 47.279255124595362 uW/cm2
                value: 47.279255124595362,
                color: COLORS.ORANGE,
              },
              {
                value: 10000,
                color: COLORS.GREEN,
              },
            ]
          : null

      return {
        visible: visibleReadings.indexOf(x) !== -1,
        name: readingData.name,
        columnName: x,
        turboThreshold: data.length > 1000 ? data.length : 1000, // 1000 is highcharts default
        data: data.map(y => {
          const hasAnomaly = y.anomalyReadingType === x && !!y.anomaly

          return {
            x: y.time,
            y: y[x],
            ...(isAdmin && hasAnomaly ? {
              marker: {
                lineWidth: 2,
                fillColor: COLORS.LIGHT_RED,
                lineColor: COLORS.DARK_RED,
                symbol: 'circle',
                radius: 7,
                enabled: true,
              },
            } : {}),
          }
        }),
        color: readingData.color,
        tooltip: {
          valueSuffix: readingData.unit,
        },
        zones,
      }
    }),
  }

  return (
    <div className="SensorChart" onClick={() => setChartUpdated(Math.random)}>
      {isLoading && !data.length ? (
        <Spin size="large" className="w-100 center mv6" />
      ) : data.length ? (
        <div className="mb3">
          <div className="flex items-center mb2">
            <span className="mr2 f6 b">Chart Type:</span>
            <RadioGroup value={chartType} size="small">
              <RadioButton value="line" onClick={() => setChartType('line')}>Line</RadioButton>
              <RadioButton value="area" onClick={() => setChartType('area')}>Area</RadioButton>
              <RadioButton value="column" onClick={() => setChartType('column')}>Column</RadioButton>
              <RadioButton value="scatter" onClick={() => setChartType('scatter')}>Scatter</RadioButton>
            </RadioGroup>
          </div>
          <HighchartsReact
            key={chartType}
            highcharts={Highcharts}
            options={chartOptions}
            containerProps={{ style: { height: '400px' } }}
          />

          {data.length > 1 && (
            <div className="mb3 tc b f5">
              Sensor data available from {dateTimeFormatter(data[0].time, true)}{' '}
              to {dateTimeFormatter(data[data.length - 1].time, true)}
            </div>
          )}

          {visibleColumns.length > 0 && (
            <div className="mb3">
              <Collapse>
                <Panel header="Units" key="units">
                  <div className="flex flex-wrap">
                    {visibleColumns.map(x => {
                      const readingData = getSensorReading(x)
                      return (
                        !!readingData.unit && (
                          <div key={x} className="w-25-ns w-100 flex">
                            <div className="b mr1">{readingData.name}:</div>
                            <div>{readingData.unit}</div>
                          </div>
                        )
                      )
                    })}
                  </div>
                </Panel>
              </Collapse>
            </div>
          )}

          {isAdmin && (
            <div className="ba b--yellow br2 pa2 mb3 bg-light-yellow">
              <H4>Anomaly Detection (Alpha)</H4>
              <div className="flex items-center mb3 b">
                <div>
                  <img
                    src={anomalyMarker}
                    alt="Anomaly Marker"
                    width={26}
                    height={26}
                  />
                </div>
                <div>
                  Points with this marker have been detected as anomalies.{' '}
                </div>
              </div>
              <div>
                This feature is currently in development and only visible to
                Pillar Admins.
              </div>
            </div>
          )}
        </div>
      ) : (
        <H4 className="tc">No Data Available for Selected Date Range</H4>
      )}
    </div>
  )
}

SensorChart.propTypes = {
  data: PropTypes.arrayOf(PropTypes.object).isRequired,
  columns: PropTypes.arrayOf(PropTypes.string).isRequired,
  updateQueryParams: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
  locationName: PropTypes.string,
  podPillarId: PropTypes.string.isRequired,
  isLeakPod: PropTypes.bool.isRequired,
  isEvacPod: PropTypes.bool.isRequired,
}

export default SensorChart
